// src/components/employee-steps/StepSensitiveInfo.js
//
// Add Employee wizard step — combines the three field groups that move to the
// PII subcollection in v2.0. Runs as step 2 (after Personal, before Employment)
// under the BL-063 salary-first reorder, so the salary entered here is available
// to the SSS suggestion chip in the Employment step. Field groups:
//
//   1. Compensation (salary)
//   2. Banking (account number, account name)
//   3. Government IDs (SSS, PhilHealth, Pag-IBIG, TIN)
//
// Visibility is gated at the wizard level on emp:pii:edit. Users without
// emp:pii:edit (e.g. Manage Employee = Edit + Sensitive Info = None custom
// role) skip this step entirely; the wizard advances directly to Review.
//
// Lock-icon badge in the section header + "PII subcollection" hint reinforce
// the access model. PRD_UAM_CustomRoles2.md §6.1.

import React, { useEffect, useRef } from "react";
import Icon from "../icons/Icon";

export const StepSensitiveInfo = ({
  formData,
  errors,
  handleInputChange,
  handleNext,
  handlePrevious,
  // PRD_CbaPremiumBase C12(a). The company's saved minimum daily wage, fetched
  // by AddEmployee (this step is presentational and reads no Firestore).
  // `null` means "unknown" — the read failed — and must NOT block: C12(b) is
  // the backstop, and a failed settings read has no business stopping an
  // operator from saving an employee.
  companyMinimumDailyWage = null,
}) => {
  const firstInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  // ============================================================
  // Salary helpers
  // ============================================================

  const formatSalary = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const numStr = value.toString().replace(/[^0-9.]/g, "");
    if (!numStr) return "";
    const parts = numStr.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const isMonthly = formData.employment?.salaryType === "monthly";

  const handleSalaryChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    handleInputChange("employment", isMonthly ? "monthlySalary" : "dailySalary", value);
  };

  // PRD_MonthlySalaryType: salary type is a real choice now. Switching clears
  // the other type's amount so stale values never ride along (they're
  // mutually exclusive in the PII compensation block).
  const handleSalaryTypeChange = (e) => {
    const type = e.target.value;
    handleInputChange("employment", "salaryType", type);
    handleInputChange("employment", type === "monthly" ? "dailySalary" : "monthlySalary", "");
    if (type !== "monthly") {
      // M4: the toggle only means something for monthly-paid staff.
      handleInputChange("employment", "payBasis", "fixed");
    }
    if (type === "monthly") {
      // C5: the CBA premium base applies to daily-paid employees only (C2), so
      // clear it rather than leaving a written-but-never-read flag behind.
      handleInputChange("employment", "cbaPremiumBase", false);
    }
  };

  // C12(a): the switch cannot be turned on while the company has no usable
  // minimum daily wage — the premiums would have nothing to price against.
  // `null` (unknown, the read failed) deliberately does not block.
  const cbaBlockedReason =
    companyMinimumDailyWage !== null && Number(companyMinimumDailyWage) <= 0
      ? "Set a minimum daily wage in Settings → Pay Rates first."
      : null;

  const handleCbaPremiumBaseChange = (e) => {
    const next = e.target.checked;
    if (next && cbaBlockedReason) return;
    handleInputChange("employment", "cbaPremiumBase", next);
  };

  // One-way: cannot be turned ON without a usable wage, but an already-on flag
  // must always be clearable — that employee is the one C12(b) has just excluded
  // from payroll, and this is where an operator would come to fix it. This is
  // also what makes the `next && cbaBlockedReason` guard above live rather than
  // dead code.
  //
  // Computed ONCE and used for both the input's `disabled` and the label's
  // styling. Keeping them separate is what let the label go on greying itself
  // out from `cbaBlockedReason` alone after the disable became one-way — the
  // control read as un-clickable in exactly the case where it is clickable.
  // `PayrollRatesPage.js` uses one value for both for the same reason.
  const cbaDisabled =
    Boolean(cbaBlockedReason) && formData.employment?.cbaPremiumBase !== true;

  // ============================================================
  // Government ID format helpers (lifted from the deleted
  // StepGovernmentIds.js; format-on-type for Philippine ID layouts)
  // ============================================================

  const formatSSS = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 9)
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 9)}-${numbers.slice(9, 10)}`;
  };

  const formatTIN = (value) => {
    const numbers = value.replace(/\D/g, "");
    const groups = [];
    for (let i = 0; i < numbers.length; i += 3) {
      groups.push(numbers.slice(i, i + 3));
    }
    return groups.join("-");
  };

  const formatPhilHealth = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 12)}`;
  };

  const formatPagIbig = (value) => {
    const numbers = value.replace(/\D/g, "");
    const groups = [];
    for (let i = 0; i < numbers.length; i += 4) {
      groups.push(numbers.slice(i, i + 4));
    }
    return groups.join("-");
  };

  const validateSSS = (value) => value.replace(/\D/g, "").length === 10;
  const validateTIN = (value) => value.replace(/\D/g, "").length === 12;
  const validatePhilHealth = (value) => value.replace(/\D/g, "").length === 12;
  const validatePagIbig = (value) => value.replace(/\D/g, "").length === 12;

  const handleGovIdInput = (field, rawValue, formatter) => {
    handleInputChange("governmentIds", field, formatter(rawValue));
  };

  // ============================================================
  // Step-level "is valid" — disables Next button until all required
  // fields pass format validation
  // ============================================================

  const salaryAmount = isMonthly
    ? formData.employment?.monthlySalary
    : formData.employment?.dailySalary;
  const isStepComplete =
    !!salaryAmount &&
    parseFloat(salaryAmount) > 0 &&
    !!formData.banking?.accountNumber?.trim() &&
    !!formData.governmentIds?.sss &&
    !!formData.governmentIds?.philhealth &&
    !!formData.governmentIds?.pagibig &&
    !!formData.governmentIds?.tin &&
    validateSSS(formData.governmentIds.sss) &&
    validatePhilHealth(formData.governmentIds.philhealth) &&
    validatePagIbig(formData.governmentIds.pagibig) &&
    validateTIN(formData.governmentIds.tin);

  return (
    <div className="add-employee-form-section">
      {/* Step header with PII lock badge */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          padding: "0.75rem 1rem",
          marginBottom: "1.5rem",
          background: "var(--ui-accent-tint)",
          border: "1px solid var(--ui-accent)",
          borderRadius: "8px",
        }}
      >
        <span style={{ fontSize: "1.25rem" }}><Icon name="lock" size={18} /></span>
        <div style={{ fontWeight: 600, color: "var(--ui-warning-text)", fontSize: "0.875rem" }}>
          Sensitive Info — PII protected
        </div>
      </div>

      {/* ============================================================ */}
      {/* Compensation */}
      {/* ============================================================ */}
      <div style={{ marginBottom: "2rem" }}>
        <div className="form-section-header">
          <div className="form-section-icon">
            <Icon name="dollar-sign" />
          </div>
          <h2 className="form-section-title">Compensation</h2>
        </div>

        <div className="employment-form-row">
          {/* Salary Type (PRD_MonthlySalaryType: daily earn-up / monthly deduct-down) */}
          <div className="form-group">
            <label className="form-label">
              Salary Type <span className="required">*</span>
            </label>
            <select
              value={formData.employment?.salaryType || "daily"}
              onChange={handleSalaryTypeChange}
              className="form-select"
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
            </select>
            <small className="form-help-text">
              {isMonthly
                ? "Fixed monthly salary, paid as a half each cutoff"
                : "Paid per day worked, from time records"}
            </small>
          </div>

          {/* Salary amount (daily or monthly) */}
          <div className="form-group">
            <label className="form-label">
              {isMonthly ? "Monthly Salary" : "Daily Salary"}{" "}
              <span className="required">*</span>
            </label>
            <div className="input-group">
              <span className="input-group__addon">₱</span>
              <input
                ref={firstInputRef}
                type="text"
                value={formatSalary(
                  (isMonthly
                    ? formData.employment?.monthlySalary
                    : formData.employment?.dailySalary) || ""
                )}
                onChange={handleSalaryChange}
                className={`form-input form-input--right ${errors.salary ? "error" : ""}`}
                placeholder="0.00"
                style={{ textAlign: "right" }}
              />
            </div>
            {errors.salary && (
              <span className="form-error-text">{errors.salary}</span>
            )}
            <small className="form-help-text">
              {isMonthly ? "Enter monthly salary amount" : "Enter daily salary amount"}
            </small>
          </div>
        </div>

        {/* C3/C5 — the CBA premium base. Full-width row BELOW the salary
            two-column grid, mirroring "Attendance affects pay" below: nested
            inside the salary field's own form-group it rendered cramped in the
            right column with the label wrapping. The two are mutually exclusive
            (this is daily-only, that is monthly-only), so they share the slot. */}
        {!isMonthly && (
          <div className="form-group" style={{ marginTop: "0.5rem" }}>
            <label
              className="form-label"
              htmlFor="cba-premium-base"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "fit-content",
                marginBottom: 4,
                // One value, two consumers. Keeping the label's styling on a
                // separate expression is what let it go on greying itself out
                // after the disable became one-way — the control then READ as
                // un-clickable in exactly the case where it is clickable.
                cursor: cbaDisabled ? "default" : "pointer",
                opacity: cbaDisabled ? 0.6 : 1,
              }}
            >
              <input
                id="cba-premium-base"
                type="checkbox"
                checked={formData.employment?.cbaPremiumBase === true}
                disabled={cbaDisabled}
                onChange={handleCbaPremiumBaseChange}
                style={{ width: "auto", margin: 0, flexShrink: 0 }}
              />
              Price premiums on the minimum wage (CBA)
            </label>
            <small className="form-help-text">
              {cbaBlockedReason ||
                "Overtime, night differential and undertime use the company minimum daily wage instead of this employee's own rate — for whichever of those Settings → Pay Rates has enabled."}
            </small>
          </div>
        )}

        {/* M4 — Attendance affects pay (monthly only; parent-doc pay-policy
            flag, default Off = fixed salary) */}
        {isMonthly && (
          <div className="form-group" style={{ marginTop: "0.5rem" }}>
            <label
              className="form-label"
              htmlFor="attendance-affects-pay"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "fit-content",
                marginBottom: 4,
                cursor: "pointer",
              }}
            >
              <input
                id="attendance-affects-pay"
                type="checkbox"
                checked={formData.employment?.payBasis === "attendance"}
                onChange={(e) =>
                  handleInputChange(
                    "employment",
                    "payBasis",
                    e.target.checked ? "attendance" : "fixed"
                  )
                }
                style={{ width: "auto", margin: 0, flexShrink: 0 }}
              />
              Attendance affects pay
            </label>
            <small className="form-help-text">
              {formData.employment?.payBasis === "attendance"
                ? "On — overtime and night premiums are added; unpaid absences and undertime are deducted, based on time records."
                : "Off — fixed salary: always paid in full, regardless of time records."}
            </small>
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/* Banking */}
      {/* ============================================================ */}
      <div style={{ marginBottom: "2rem" }}>
        <div className="form-section-header">
          <div className="form-section-icon">
            <Icon name="dollar-sign" />
          </div>
          <h2 className="form-section-title">Bank Details</h2>
        </div>

        <div className="employment-form-row">
          <div className="form-group">
            <label className="form-label">
              Account Number <span className="required">*</span>
            </label>
            <input
              type="text"
              value={formData.banking?.accountNumber || ""}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                handleInputChange("banking", "accountNumber", value);
              }}
              className={`form-input ${errors.accountNumber ? "error" : ""}`}
              placeholder="Enter account number"
              maxLength="16"
            />
            {errors.accountNumber && (
              <span className="form-error-text">{errors.accountNumber}</span>
            )}
            <small className="form-help-text">
              Numbers only — for direct salary deposit
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Account Name</label>
            <input
              type="text"
              value={formData.banking?.accountName || ""}
              onChange={(e) =>
                handleInputChange("banking", "accountName", e.target.value)
              }
              className={`form-input ${errors.accountName ? "error" : ""}`}
              placeholder="As registered with the bank"
            />
            {errors.accountName && (
              <span className="form-error-text">{errors.accountName}</span>
            )}
            <small className="form-help-text">
              Should match the name on the bank account
            </small>
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* Government IDs */}
      {/* ============================================================ */}
      <div style={{ marginBottom: "2rem" }}>
        <div className="form-section-header">
          <div className="form-section-icon">
            <Icon name="landmark" />
          </div>
          <h2 className="form-section-title">Government IDs</h2>
        </div>

        <div className="form-grid">
          {/* SSS */}
          <div className="form-group">
            <label className="form-label">
              SSS Number <span className="required">*</span>
            </label>
            <input
              type="text"
              value={formData.governmentIds?.sss || ""}
              onChange={(e) => handleGovIdInput("sss", e.target.value, formatSSS)}
              placeholder="XX-XXXXXXX-X"
              maxLength={12}
              className={`form-input form-input--monospace ${errors.sss ? "form-input--error" : ""}`}
            />
            {errors.sss && <span className="form-error-text">{errors.sss}</span>}
            <p className="form-help-text">
              Social Security System — Format: XX-XXXXXXX-X (10 digits)
              {formData.governmentIds?.sss && (
                <span
                  className={`form-validation ${validateSSS(formData.governmentIds.sss) ? "form-validation--valid" : "form-validation--invalid"}`}
                >
                  {validateSSS(formData.governmentIds.sss)
                    ? <><Icon name="check" size={12} aria-hidden="true" /> Valid format</>
                    : <><Icon name="x" size={12} aria-hidden="true" /> Invalid format (need 10 digits)</>}
                </span>
              )}
            </p>
          </div>

          {/* PhilHealth */}
          <div className="form-group">
            <label className="form-label">
              PhilHealth Number <span className="required">*</span>
            </label>
            <input
              type="text"
              value={formData.governmentIds?.philhealth || ""}
              onChange={(e) => handleGovIdInput("philhealth", e.target.value, formatPhilHealth)}
              placeholder="XX-XXXXXXXXXX"
              maxLength={14}
              className={`form-input form-input--monospace ${errors.philhealth ? "form-input--error" : ""}`}
            />
            {errors.philhealth && <span className="form-error-text">{errors.philhealth}</span>}
            <p className="form-help-text">
              Philippine Health Insurance — Format: XX-XXXXXXXXXX (12 digits)
              {formData.governmentIds?.philhealth && (
                <span
                  className={`form-validation ${validatePhilHealth(formData.governmentIds.philhealth) ? "form-validation--valid" : "form-validation--invalid"}`}
                >
                  {validatePhilHealth(formData.governmentIds.philhealth)
                    ? <><Icon name="check" size={12} aria-hidden="true" /> Valid format</>
                    : <><Icon name="x" size={12} aria-hidden="true" /> Invalid format (need 12 digits)</>}
                </span>
              )}
            </p>
          </div>

          {/* Pag-IBIG */}
          <div className="form-group">
            <label className="form-label">
              Pag-IBIG Number <span className="required">*</span>
            </label>
            <input
              type="text"
              value={formData.governmentIds?.pagibig || ""}
              onChange={(e) => handleGovIdInput("pagibig", e.target.value, formatPagIbig)}
              placeholder="XXXX-XXXX-XXXX"
              maxLength={14}
              className={`form-input form-input--monospace ${errors.pagibig ? "form-input--error" : ""}`}
            />
            {errors.pagibig && <span className="form-error-text">{errors.pagibig}</span>}
            <p className="form-help-text">
              Home Development Mutual Fund — Format: XXXX-XXXX-XXXX (12 digits)
              {formData.governmentIds?.pagibig && (
                <span
                  className={`form-validation ${validatePagIbig(formData.governmentIds.pagibig) ? "form-validation--valid" : "form-validation--invalid"}`}
                >
                  {validatePagIbig(formData.governmentIds.pagibig)
                    ? <><Icon name="check" size={12} aria-hidden="true" /> Valid format</>
                    : <><Icon name="x" size={12} aria-hidden="true" /> Invalid format (need 12 digits)</>}
                </span>
              )}
            </p>
          </div>

          {/* TIN */}
          <div className="form-group">
            <label className="form-label">
              TIN (Tax Identification Number) <span className="required">*</span>
            </label>
            <input
              type="text"
              value={formData.governmentIds?.tin || ""}
              onChange={(e) => handleGovIdInput("tin", e.target.value, formatTIN)}
              placeholder="XXX-XXX-XXX-XXX"
              maxLength={15}
              className={`form-input form-input--monospace ${errors.tin ? "form-input--error" : ""}`}
            />
            {errors.tin && <span className="form-error-text">{errors.tin}</span>}
            <p className="form-help-text">
              Bureau of Internal Revenue — Format: XXX-XXX-XXX-XXX (12 digits)
              {formData.governmentIds?.tin && (
                <span
                  className={`form-validation ${validateTIN(formData.governmentIds.tin) ? "form-validation--valid" : "form-validation--invalid"}`}
                >
                  {validateTIN(formData.governmentIds.tin)
                    ? <><Icon name="check" size={12} aria-hidden="true" /> Valid format</>
                    : <><Icon name="x" size={12} aria-hidden="true" /> Invalid format (need 12 digits)</>}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="add-employee-form-actions">
        <button onClick={handlePrevious} className="btn btn--secondary">
          <Icon name="arrow-left" size={14} aria-hidden="true" /> Previous
        </button>
        <button
          onClick={handleNext}
          className="btn btn--primary"
          disabled={!isStepComplete}
        >
          Next: Employment →
        </button>
      </div>
    </div>
  );
};
